import { Pencil, Trash2 } from 'lucide-react'
import type { TimeBlock } from '@/shared/domain'
import { Badge, Button, Switch } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'

export type TimeBlockRowProps = {
  block: TimeBlock
  timeRange: string
  periodLabel: string
  isSaving: boolean
  onToggleEnabled: (block: TimeBlock, enabled: boolean) => void
  onEdit: (block: TimeBlock) => void
  onDelete: (block: TimeBlock) => void
}

export function TimeBlockRow({
  block,
  timeRange,
  periodLabel,
  isSaving,
  onToggleEnabled,
  onEdit,
  onDelete,
}: TimeBlockRowProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 py-3',
        block.enabled ? null : 'opacity-60',
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium">{block.name}</span>
          <Badge variant="secondary" className="shrink-0 text-2xs">
            {periodLabel}
          </Badge>
          {block.enabled ? null : (
            <Badge variant="outline" className="shrink-0 text-2xs">
              Off
            </Badge>
          )}
        </div>
        <p className="font-mono text-xs text-muted-foreground">{timeRange}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Switch
          checked={block.enabled}
          disabled={isSaving}
          onCheckedChange={(checked) => onToggleEnabled(block, checked)}
          aria-label={`Enable time block ${block.name}`}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={isSaving}
          onClick={() => onEdit(block)}
          aria-label={`Edit time block ${block.name}`}
        >
          <Pencil className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={isSaving}
          onClick={() => onDelete(block)}
          aria-label={`Delete time block ${block.name}`}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  )
}
